import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PenLine, Copy, Play, RefreshCw, Plus, Eye, Send, Bell, FileCheck, Edit3, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, eur, dt } from "../components/common";
import { Modal, Field, Selecter } from "./crm/crmui";
import ContractDrawer from "../components/ContractDrawer";
import { useAutoRefresh } from "../lib/autoRefresh";

const SIG_STATE_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  sent: "bg-amber-100 text-amber-800",
  signed: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  expired: "bg-red-100 text-red-800",
};

export default function Contracts() {
  const [rows, setRows] = useState([]);
  const [signed, setSigned] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const [genPid, setGenPid] = useState("");
  const [showSign, setShowSign] = useState(null);
  const [sigStatus, setSigStatus] = useState(null);
  const [signer, setSigner] = useState({ signatario_nome: "", signatario_email: "", signatario_telefone: "", email: true, sms: false });
  const [drawer, setDrawer] = useState(null);
  const [external, setExternal] = useState(null);
  const [editBody, setEditBody] = useState(null);
  const [renewal, setRenewal] = useState(null); // { contract, preview, loading, result }

  const openRenewalWizard = async (c) => {
    setRenewal({ contract: c, preview: null, loading: true, result: null });
    try {
      const { data } = await api.get(`/cpq/contracts/${c.id}/renewal-preview`);
      setRenewal({ contract: c, preview: data, loading: false, result: null });
    } catch (e) {
      setRenewal(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const confirmRenewal = async () => {
    try {
      const { data } = await api.post(`/cpq/contracts/${renewal.contract.id}/renewal`);
      setRenewal((r) => ({ ...r, result: data }));
      toast.success(data.reutilizada ? "Renovacao ja existente carregada." : "Proposta de renovacao criada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [c, p, a] = await Promise.all([
        api.get("/cpq/contracts"), api.get("/cpq/proposals", { params: { estado: "assinada" } }), api.get("/crm/accounts"),
      ]);
      setRows(c.data); setSigned(p.data); setAccounts(a.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line
  const accName = (id) => accounts.find((a) => a.id === id)?.nome || "-";

  const gerar = async () => {
    if (!genPid) return toast.error("Selecione uma proposta assinada.");
    try { await api.post(`/cpq/contracts/from-proposal/${genPid}`, { duracao_meses: 12 }); toast.success("Contrato gerado."); setShowGen(false); setGenPid(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openPdf = async (id) => { try { const { data } = await api.get(`/cpq/contracts/${id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); } catch (e) { toast.error("Erro ao abrir PDF."); } };
  const ativar = async (id) => { try { await api.post(`/cpq/contracts/${id}/ativar`); toast.success("Contrato ativado."); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const renovar = async (id) => { try { await api.post(`/cpq/contracts/${id}/renovar`, { duracao_meses: 12 }); toast.success("Contrato renovado (+12 meses)."); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const openSign = async (c) => {
    setShowSign(c); setSigStatus(null);
    setSigner({ signatario_nome: "", signatario_email: "", signatario_telefone: "", email: true, sms: false });
    try { const { data } = await api.get(`/cpq/contracts/${c.id}/signature`); if (data.exists) setSigStatus(data); } catch (e) { /* noop */ }
  };
  const sendSign = async () => {
    if (!signer.signatario_nome.trim()) return toast.error("Indique o nome do signatario.");
    const canais = [signer.email && "email", signer.sms && "sms"].filter(Boolean);
    try {
      await api.post(`/cpq/contracts/${showSign.id}/enviar-assinatura`, { signatario_nome: signer.signatario_nome, signatario_email: signer.signatario_email, signatario_telefone: signer.signatario_telefone, canais });
      toast.success("Enviado para assinatura.");
      const { data } = await api.get(`/cpq/contracts/${showSign.id}/signature`); setSigStatus(data); load();
    } catch (e) {
      if (e.response?.status === 409) toast.warning(formatApiErrorDetail(e.response?.data?.detail) + " Aprove em Aprovacoes.");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };
  const copyLink = (token) => { navigator.clipboard.writeText(`${window.location.origin}/assinar/${token}`); toast.success("Link copiado."); };

  const openExternal = async (c) => {
    setExternal({ contract: c, status: null, loading: true });
    try {
      const { data } = await api.get(`/signature-providers/contracts/${c.id}/status`, { params: { refresh: true } });
      setExternal({ contract: c, status: data, loading: false });
    } catch (e) { setExternal({ contract: c, status: null, loading: false, error: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const sendExternal = async () => {
    try {
      await api.post(`/signature-providers/contracts/${external.contract.id}/send`, {});
      toast.success("Contrato enviado para assinatura externa.");
      openExternal(external.contract); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remindExternal = async () => {
    try {
      await api.post(`/signature-providers/contracts/${external.contract.id}/remind`);
      toast.success("Lembrete enviado.");
      openExternal(external.contract);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openSignedPdf = async (cid) => {
    try {
      const { data } = await api.get(`/signature-providers/contracts/${cid}/pdf-signed`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF assinado ainda indisponivel."); }
  };
  const saveBody = async () => {
    try {
      await api.put(`/cpq/contracts/${editBody.id}/corpo`, { corpo_render: editBody.corpo_render, titulo: editBody.titulo });
      toast.success("Contrato atualizado.");
      setEditBody(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cols = [
    { key: "numero", label: "Numero", render: (r) => (
      <button onClick={() => setDrawer(r)} data-testid={`contract-drawer-open-${r.id}`}
        className="font-semibold text-navy hover:text-blue hover:underline text-left">{r.numero}</button>
    ) },
    { key: "titulo", label: "Titulo" },
    { key: "account_id", label: "Cliente", render: (r) => accName(r.account_id) },
    { key: "total", label: "Valor", render: (r) => eur(r.total) },
    { key: "fim", label: "Termo", render: (r) => r.fim ? dt(r.fim) : "-" },
    { key: "contrato_tipo", label: "Tipo", render: (r) => (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${r.contrato_tipo === "personalizado" ? "bg-purple-100 text-purple-800" : "bg-blue/10 text-blue"}`}>
        {r.contrato_tipo || "standard"}
      </span>
    ) },
    { key: "signature_state", label: "Assinatura", render: (r) => {
      const s = r.signature_state || (r.external_signature?.status) || "pending";
      return <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${SIG_STATE_COLORS[s] || SIG_STATE_COLORS.pending}`}>{s}</span>;
    } },
    { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
    { key: "acoes", label: "", render: (r) => {
      const sigState = r.signature_state || (r.external_signature?.status) || "pending";
      const canEdit = r.contrato_tipo === "personalizado" && sigState !== "signed" && sigState !== "sent";
      const canSend = sigState === "pending" || sigState === "declined" || sigState === "expired";
      const canRemind = sigState === "sent";
      const canViewSigned = sigState === "signed";
      return (
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setDrawer(r)} data-testid={`contract-drawer-btn-${r.id}`} title="Ver detalhes" className="text-slate-700 hover:bg-slate-100 rounded p-1"><Eye size={15} /></button>
          <button onClick={() => openPdf(r.id)} data-testid={`ctr-pdf-${r.id}`} title="PDF" className="text-blue hover:bg-blue/10 rounded p-1"><FileText size={15} /></button>
          {canEdit && <button onClick={() => setEditBody({ id: r.id, titulo: r.titulo, corpo_render: r.corpo_render || "" })}
            data-testid={`ctr-edit-body-${r.id}`} title="Editar corpo do contrato" className="text-purple-700 hover:bg-purple-50 rounded p-1"><Edit3 size={15} /></button>}
          {canSend && <button onClick={() => openExternal(r)} data-testid={`ctr-send-external-${r.id}`}
            title="Enviar para assinatura externa" className="text-navy hover:bg-tint rounded p-1"><Send size={15} /></button>}
          {canRemind && <button onClick={() => openExternal(r)} data-testid={`ctr-remind-${r.id}`}
            title="Ver / lembrar assinatura" className="text-amber-700 hover:bg-amber-50 rounded p-1"><Bell size={15} /></button>}
          {canViewSigned && <button onClick={() => openSignedPdf(r.id)} data-testid={`ctr-signed-pdf-${r.id}`}
            title="PDF assinado" className="text-green-700 hover:bg-green-50 rounded p-1"><FileCheck size={15} /></button>}
          {r.estado !== "assinada" && r.estado !== "ativo" && <button onClick={() => openSign(r)} data-testid={`ctr-sign-${r.id}`} title="Assinatura interna (OTP)" className="text-navy hover:bg-tint rounded p-1"><PenLine size={15} /></button>}
          {r.estado === "assinada" && <button onClick={() => ativar(r.id)} data-testid={`ctr-activate-${r.id}`} title="Ativar" className="text-green-600 hover:bg-green-50 rounded p-1"><Play size={15} /></button>}
          {r.estado === "ativo" && <button onClick={() => renovar(r.id)} data-testid={`ctr-renew-${r.id}`} title="Renovar" className="text-navy hover:bg-tint rounded p-1"><RefreshCw size={15} /></button>}
          {r.estado === "ativo" && <button onClick={() => openRenewalWizard(r)} data-testid={`ctr-renewal-wizard-${r.id}`} title="Wizard de renovacao (gera proposta)" className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><Sparkles size={15} /></button>}
        </div>
      );
    } },
  ];

  return (
    <>
      <PageHeader overline="CPQ" title="Contratos" action={<Btn onClick={() => setShowGen(true)} testid="gen-contract-btn"><Plus size={16} /> Gerar de proposta</Btn>} />
      <Table testid="contracts-table" columns={cols} rows={rows} />

      <Modal open={showGen} onClose={() => setShowGen(false)} title="Gerar contrato de proposta assinada" testid="gen-contract-modal">
        {signed.length === 0 ? <p className="text-sm text-muted2">Nao ha propostas assinadas. Assine uma proposta primeiro.</p> : (
          <>
            <Selecter label="Proposta assinada" value={genPid} onChange={setGenPid} options={[{ value: "", label: "- Selecionar -" }, ...signed.map((p) => ({ value: p.id, label: `${p.numero} - ${p.titulo}` }))]} testid="gen-proposal-select" />
            <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowGen(false)}>Cancelar</Btn><Btn onClick={gerar} testid="gen-contract-save">Gerar contrato</Btn></div>
          </>
        )}
      </Modal>

      <Modal open={!!showSign} onClose={() => setShowSign(null)} title={`Assinatura - ${showSign?.numero || ""}`} testid="ctr-signature-modal">
        {sigStatus?.exists ? (
          <div className="space-y-3" data-testid="ctr-signature-status">
            <div className="flex items-center gap-2"><span className="text-sm font-semibold text-navy">Estado:</span> <Badge value={sigStatus.estado} /></div>
            {sigStatus.otp_fallback && <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm" data-testid="ctr-otp-fallback">Codigo (nao entregue): <b className="text-lg">{sigStatus.otp_fallback}</b></div>}
            <div className="flex items-center gap-2">
              <input readOnly value={`${window.location.origin}/assinar/${sigStatus.token}`} className="flex-1 px-2 py-1.5 border border-tint rounded text-xs bg-tint/40" data-testid="ctr-sign-link" />
              <Btn variant="ghost" onClick={() => copyLink(sigStatus.token)}><Copy size={14} /> Copiar</Btn>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Nome do signatario" value={signer.signatario_nome} onChange={(v) => setSigner({ ...signer, signatario_nome: v })} testid="ctr-signer-nome" />
            <Field label="Email" value={signer.signatario_email} onChange={(v) => setSigner({ ...signer, signatario_email: v })} testid="ctr-signer-email" />
            <Field label="Telefone (E.164)" value={signer.signatario_telefone} onChange={(v) => setSigner({ ...signer, signatario_telefone: v })} testid="ctr-signer-telefone" />
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={signer.email} onChange={(e) => setSigner({ ...signer, email: e.target.checked })} data-testid="ctr-canal-email" /> Email</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={signer.sms} onChange={(e) => setSigner({ ...signer, sms: e.target.checked })} data-testid="ctr-canal-sms" /> SMS</label>
            </div>
            <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShowSign(null)}>Cancelar</Btn><Btn onClick={sendSign} testid="ctr-send-signature">Enviar para assinatura</Btn></div>
          </div>
        )}
      </Modal>
      {drawer && (
        <ContractDrawer
          contract={drawer}
          open={!!drawer}
          onClose={() => setDrawer(null)}
          accounts={accounts}
          onOpenPdf={(c) => openPdf(c.id)}
          onOpenSign={(c) => { setDrawer(null); openSign(c); }}
          onActivate={(c) => { ativar(c.id); setDrawer(null); }}
          onRenew={(c) => { renovar(c.id); setDrawer(null); }}
          onRenewalWizard={(c) => { setDrawer(null); openRenewalWizard(c); }}
        />
      )}

      <Modal open={!!renewal} onClose={() => setRenewal(null)} title={`Renovacao - ${renewal?.contract?.numero || ""}`} testid="renewal-wizard-modal">
        {renewal?.loading ? <p className="text-sm text-muted2">A carregar...</p> :
          renewal?.result ? (
            <div className="space-y-3" data-testid="renewal-result">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
                <b>{renewal.result.reutilizada ? "Proposta existente" : "Nova proposta criada"}</b>
                <div className="mt-1 text-xs">
                  Numero: <b>{renewal.result.proposal?.numero}</b><br />
                  Estado: <b>{renewal.result.proposal?.estado}</b><br />
                  Total: <b>{eur(renewal.result.proposal?.total || 0)}</b>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setRenewal(null)}>Fechar</Btn>
                <Btn onClick={() => { window.location.href = "/app/propostas"; }} testid="renewal-open-proposals">Ver propostas</Btn>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="renewal-preview">
              <div className="bg-slate-50 border border-tint rounded p-3 text-sm">
                <div className="text-xs text-muted2 mb-2">Contrato origem</div>
                <div><b>{renewal?.preview?.contrato?.numero}</b> - {renewal?.preview?.contrato?.titulo}</div>
                <div className="text-xs mt-1">
                  Fim: <b>{renewal?.preview?.contrato?.fim ? dt(renewal.preview.contrato.fim) : "-"}</b> ({renewal?.preview?.dias_ate_fim} dias)<br />
                  Duracao actual: <b>{renewal?.preview?.contrato?.duracao_meses} meses</b><br />
                  Valor: <b>{eur(renewal?.preview?.contrato?.total || 0)}</b>
                </div>
              </div>
              {renewal?.preview?.existing_proposal && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
                  Ja existe proposta <b>{renewal.preview.existing_proposal.numero}</b> ({renewal.preview.existing_proposal.estado}) para este contrato. Sera reutilizada.
                </div>
              )}
              <p className="text-xs text-muted2">
                A wizard vai criar uma nova proposta em <b>rascunho</b> com os mesmos itens do contrato,
                validade de 30 dias e ligacao ao contrato de origem. Podera rever e enviar depois.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Btn variant="ghost" onClick={() => setRenewal(null)}>Cancelar</Btn>
                <Btn onClick={confirmRenewal} testid="renewal-confirm">Gerar proposta de renovacao</Btn>
              </div>
            </div>
          )}
      </Modal>

      <Modal open={!!external} onClose={() => setExternal(null)} title={`Assinatura externa - ${external?.contract?.numero || ""}`} testid="ext-sig-modal">
        {external?.loading ? <p className="text-sm text-muted2">A carregar...</p> : (
          !external?.status?.exists ? (
            <div className="space-y-3">
              <p className="text-sm text-muted2">Este contrato ainda nao foi enviado para nenhum provider de assinatura externa (DocuSign / Dropbox Sign).</p>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded">
                Certifique-se que ha um provider marcado como ativo em Definicoes &gt; Assinatura Digital
                e que o cliente tem email preenchido.
              </p>
              <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setExternal(null)}>Fechar</Btn>
                <Btn onClick={sendExternal} testid="ext-send-btn">Enviar para assinatura</Btn></div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ext-sig-status">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted2">Provider:</span> <b className="text-navy">{external.status.provider}</b></div>
                <div><span className="text-muted2">Estado:</span>
                  <span className={`ml-1 text-xs px-2 py-0.5 rounded font-semibold ${SIG_STATE_COLORS[external.status.status] || SIG_STATE_COLORS.pending}`}>{external.status.status}</span>
                </div>
                <div><span className="text-muted2">Signatario:</span> <b className="text-navy">{external.status.signer_name}</b></div>
                <div><span className="text-muted2">Email:</span> <span className="text-navy text-xs">{external.status.signer_email}</span></div>
                <div><span className="text-muted2">Enviado em:</span> <span className="text-xs">{external.status.enviado_em ? dt(external.status.enviado_em) : "-"}</span></div>
                <div><span className="text-muted2">Lembretes:</span> <b>{external.status.n_lembretes || 0}</b></div>
              </div>
              {external.status.eventos?.length > 0 && (
                <div className="border-t border-tint pt-2">
                  <div className="text-xs font-semibold text-navy mb-1">Historico</div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {external.status.eventos.map((ev) => (
                      <div key={ev.id} className="text-xs flex gap-2">
                        <span className="text-muted2 w-32">{dt(ev.em)}</span>
                        <span className="font-semibold text-navy">{ev.event_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Btn variant="ghost" onClick={() => setExternal(null)}>Fechar</Btn>
                {external.status.status === "signed" && (
                  <Btn onClick={() => openSignedPdf(external.contract.id)} testid="ext-open-signed">Ver PDF assinado</Btn>
                )}
                {(external.status.status === "sent") && (
                  <Btn onClick={remindExternal} testid="ext-remind-btn">Reenviar lembrete</Btn>
                )}
                {(["declined", "expired"].includes(external.status.status)) && (
                  <Btn onClick={sendExternal} testid="ext-resend-btn">Reenviar</Btn>
                )}
              </div>
            </div>
          )
        )}
      </Modal>

      <Modal open={!!editBody} onClose={() => setEditBody(null)} title={`Editar contrato - ${editBody?.titulo || ""}`} testid="edit-body-modal">
        {editBody && (
          <div className="space-y-3">
            <Field label="Titulo" value={editBody.titulo} onChange={(v) => setEditBody({ ...editBody, titulo: v })} testid="edit-body-titulo" />
            <label className="block text-sm font-semibold text-navy mb-1">Corpo do contrato (texto completo)</label>
            <textarea value={editBody.corpo_render}
              onChange={(e) => setEditBody({ ...editBody, corpo_render: e.target.value })}
              rows={18} data-testid="edit-body-content"
              className="w-full px-3 py-2 border border-tint rounded font-mono text-xs" />
            <p className="text-xs text-muted2">Este contrato foi marcado como <b>personalizado</b> na adjudicacao. Edite as clausulas antes de enviar para assinatura.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditBody(null)}>Cancelar</Btn>
              <Btn onClick={saveBody} testid="edit-body-save">Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
